/*
 * DashboardPage - convenient component for creating our standard sweetprocess dashboard
 * */
import React, {ReactNode, useEffect, useState} from 'react';

import {DefaultRootState, useSelector} from 'react-redux';

import {LinkPagination} from 'app/components/general/LinkPagination';
import MainCol from 'app/components/general/MainCol';
import NoResultsMessage from 'app/components/general/NoResultsMessage';
import {StyledSidebarDIV} from 'app/components/general/Sidebar';
import {DashboardLoadingContent} from 'app/documents/components/DocumentsDashboard/DashboardLoadingContent';
import {applyFilter} from 'app/general/dashboards/DashboardFilterbar';
import {DashboardItemsCounter} from 'app/general/dashboards/DashboardItemsCounter';
import {BootstrapMinMediumMediaQuery} from 'app/general/mediaqueries';
import {SPPage, SPPageProps} from 'app/general/structures/SPPage';
import {DocumentsNotificationsProvider} from 'app/notifly/components/DocumentsNotificationsProvider';
import {NotificationType} from 'app/notifly/types';
import {PresenceSubscriber} from 'app/presence/components/PresenceSubscriber';
import {
  useModelChangeSubscription,
  WSModelChangeMessageHandler,
} from 'app/subscriptions';
import {convertListResponseToStoredResponse} from 'app/tasks/components/TasksDashboard/AllTasksList/TasksLinkPagination';
import {shallowEqual} from 'app/utils/react-utils';

import type {UseQuery} from '@reduxjs/toolkit/dist/query/react/buildHooks';
import type {QueryDefinition} from '@reduxjs/toolkit/query/react';
import type {ContentType, ListResponseType} from 'sweet-types';

type UseQueryType<FP, IT> = UseQuery<
  QueryDefinition<FP, any, any, ListResponseType<IT>, 'sweetAPI'>
>;

interface Props<
  ItemType extends {id: number | string; url?: string},
  DashboardParams,
  FetchParams extends {page: number | string},
  UseQueryHookType extends UseQueryType<FetchParams, ItemType>
> extends Pick<
    SPPageProps,
    | 'breadcrumbProps'
    | 'title'
    | 'pageTitle'
    | 'subTitle'
    | 'pageActions'
    | 'actionArea'
    | 'location'
    | 'router'
  > {
  convertParamsFunc: (dashboardParams: DashboardParams) => FetchParams;
  useDataHook: UseQueryHookType;
  hookOptions?: {refetchOnMountOrArgChange?: boolean | number};
  filterbar?: (props: DashboardPassedProps<FetchParams>) => ReactNode;
  filtersSelector?: (state: DefaultRootState) => DashboardParams;
  renderItem: (
    item: ItemType,
    props: DashboardPassedProps<FetchParams>,
    notifications?: Array<NotificationType>
  ) => ReactNode;
  contentType?: ContentType;
  enableNotifications?: boolean;
  onModalChangeMessage?: (
    props: DashboardPassedProps<FetchParams>
  ) => WSModelChangeMessageHandler;
  messages: {
    counter?: (count: number) => ReactNode;
    notFound: (
      props: DashboardPassedProps<FetchParams>,
      MessageElement: typeof NoResultsMessage
    ) => ReactNode;
  };
  subscribeToPresence?: {
    contentType: ContentType;
    objectsIds: (objects: Array<ItemType>) => Array<string | number>;
  };
  sidebar?: ReactNode;
  header?: (props: DashboardPassedProps<FetchParams>) => ReactNode;
  footer?: (props: DashboardPassedProps<FetchParams>) => ReactNode;
}

export interface DashboardPassedProps<
  FetchParams extends {page: number | string}
> {
  resetFilters: () => void;
  viewingAll: boolean;
  count: number | null;
  params: FetchParams;
  refetch: () => void;
}

export function DashboardPage<
  ItemType extends {id: number | string; url?: string},
  DashboardParams extends Record<string, unknown>,
  FetchParams extends {page: number | string},
  UseQueryHookType extends UseQueryType<FetchParams, ItemType> = UseQueryType<
    FetchParams,
    ItemType
  >
>({
  location,
  useDataHook,
  hookOptions = {},
  router,
  filterbar,
  filtersSelector,
  renderItem,
  convertParamsFunc,
  onModalChangeMessage,
  messages,
  subscribeToPresence,
  contentType,
  enableNotifications,
  sidebar,
  header,
  footer,
  ...pageProps
}: Props<ItemType, DashboardParams, FetchParams, UseQueryHookType>) {
  const params = convertParamsFunc(location.query);
  const storedParams = useSelector(filtersSelector ?? (() => null));

  const [isRedirecting, setIsRedirecting] = useState(filtersSelector != null);

  const {
    data: response,
    isFetching,
    refetch,
  } = useDataHook(params, {...hookOptions, skip: isRedirecting});

  const viewingAll = React.useMemo(
    () => shallowEqual(convertParamsFunc({} as any), params),
    [location.query]
  );

  useEffect(() => {
    if (isRedirecting) {
      // when component mounts, update the location bar with query info from storage.
      applyFilter(router, location, storedParams!);
      setIsRedirecting(false);
    }
  }, []);

  const resetFilters = () => router.replace({pathname: location.pathname});

  const dashboardPassedProps: DashboardPassedProps<FetchParams> = {
    resetFilters,
    viewingAll,
    count: response != null ? response.count : null,
    params,
    refetch,
  };

  useModelChangeSubscription((message, utils) => {
    onModalChangeMessage?.(dashboardPassedProps)(message, utils);
  }, []);

  if (isRedirecting) {
    // Our SelectBase (included via most filterbar components) gets stuck with props on mount.
    // Let's avoid mounting it until we have redirected to our true end location.
    return null;
  }

  return (
    <SPPage {...pageProps}>
      <div className="row">
        {sidebar != null ? (
          <BootstrapMinMediumMediaQuery>
            <StyledSidebarDIV>{sidebar}</StyledSidebarDIV>
          </BootstrapMinMediumMediaQuery>
        ) : null}
        <MainCol noSidebar={sidebar == null}>
          {filterbar != null ? filterbar(dashboardPassedProps) : null}
          {messages.counter != null && response != null ? (
            <DashboardItemsCounter text={messages.counter(response.count)} />
          ) : null}
          {header != null && response != null
            ? header(dashboardPassedProps)
            : null}
          {response == null ||
          // avoid showing NoResultsMessage for the previous page data
          (response != null && response.count == 0 && isFetching) ? (
            <DashboardLoadingContent />
          ) : response != null && response.count > 0 ? (
            enableNotifications ? (
              <DocumentsNotificationsProvider
                contentType={contentType}
                documentsIds={response.results.map((item) => item.id)}
              >
                {(notifications) =>
                  response.results.map((item) =>
                    renderItem(
                      item,
                      dashboardPassedProps,
                      item.url != null ? notifications[item.url] : []
                    )
                  )
                }
              </DocumentsNotificationsProvider>
            ) : (
              response.results.map((item) =>
                renderItem(item, dashboardPassedProps)
              )
            )
          ) : response != null && response.count == 0 ? (
            // not found
            messages.notFound(dashboardPassedProps, NoResultsMessage)
          ) : null}
          {response != null ? (
            <LinkPagination
              className="mt-3"
              page={params.page}
              response={convertListResponseToStoredResponse(
                response,
                params.page
              )}
            />
          ) : null}
          {footer != null && response != null
            ? footer(dashboardPassedProps)
            : null}
          {response != null &&
          response.count > 0 &&
          subscribeToPresence != null ? (
            <PresenceSubscriber
              contentType={subscribeToPresence.contentType}
              objectsIds={subscribeToPresence.objectsIds(response.results)}
              stealth={true}
            />
          ) : null}
        </MainCol>
      </div>
    </SPPage>
  );
}
